import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EnhancedOpportunityForm } from "@/components/opportunity/EnhancedOpportunityForm";
import { fetchOpportunityById, updateOpportunity } from "@/services/opportunity/opportunityService";

export default function EditOpportunity() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [opportunity, setOpportunity] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOpportunity = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const data = await fetchOpportunityById(id);
        setOpportunity(data);
      } catch (err: any) {
        setError(err.message || "Failed to load opportunity");
        toast({
          title: "Error",
          description: "Failed to load opportunity details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadOpportunity();
  }, [id, toast]);

  const handleSubmit = async (values: any) => {
    if (!id) return;
    
    try {
      setIsSubmitting(true);
      
      // Format dates for API
      const formattedValues = {
        ...values,
        expected_close_date: values.expected_close_date 
          ? values.expected_close_date.toISOString().split('T')[0]
          : null,
      };
      
      await updateOpportunity(id, formattedValues);
      
      toast({
        title: "Success",
        description: "Opportunity updated successfully",
      });
      
      navigate(`/opportunities/${id}`);
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to update opportunity",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/opportunities">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Opportunities</span>
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Opportunity</h1>
        </div>
        <Card>
          <CardContent className="p-6 flex justify-center items-center min-h-[200px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !opportunity) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/opportunities">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Opportunities</span>
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Opportunity</h1>
        </div>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-destructive">{error || "Failed to load opportunity"}</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate("/opportunities")}
            >
              Back to Opportunities
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/opportunities/${id}`}>
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only">Back to Opportunity</span>
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">Edit Opportunity: {opportunity.name}</h1>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Edit Opportunity Details</CardTitle>
        </CardHeader>
        <CardContent>
          <EnhancedOpportunityForm 
            initialValues={opportunity}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitLabel="Update Opportunity"
          />
        </CardContent>
      </Card>
    </div>
  );
}